import { CategoryHelper } from '@libs/category.helper';
import type { Category } from '@models/data';
import type Locale from '@formatjs/intl-locale';

/**
 * Determines whether to show cutting cost information in the buybox based on region, category tree, and original value.
 * BuyBox is part of the product details page.
 *
 * Requirements for these implementations come from COM-9549, COM-9673, COM-9407 (  and maybe others ;)  )
 *
 * @param {Locale} locale - The locale object containing the region information.
 * @param {string} priceCountry - The price country of the session.
 * @param {Category} categoryTree - The category tree associated with the product to evaluate specific category conditions.
 * @param {boolean} originalValue - The original value indicating whether cutting cost information should be shown.
 * @returns {boolean} Returns a boolean indicating whether cutting cost information should be displayed in the buybox.
 */
export const showCuttingCostInformationInBuyBox = (
  locale: Locale,
  priceCountry: string,
  categoryTree: Category,
  originalValue: boolean
) => {
  const REGIONS_WITHOUT_CUTTING_COST_IN_BUY_BOX = ['GB', 'IE', 'BR']; // these are regions of locales; therefore, we have to us GB (instead of UK)
  const PRICE_COUNTRIES_WITHOUT_CUTTING_COST_IN_BUY_BOX = ['UK', 'IE', 'BR']; // these are priceCountries from the session; therefore, we have to use UK (instead of GB)

  // first we consider the region of the given locale (for all not .eu domains)
  if (!!locale.region && REGIONS_WITHOUT_CUTTING_COST_IN_BUY_BOX.includes(locale.region)) {
    return false;
  }

  // when we don't have a region (like on .eu) we consider the priceCountry of the session
  if (!locale.region && PRICE_COUNTRIES_WITHOUT_CUTTING_COST_IN_BUY_BOX.includes(priceCountry.toUpperCase())) {
    return false;
  }

  // special case for US, CA and MX categories
  if (
    (locale.region === 'US' || locale.region === 'CA' || locale.region === 'MX') &&
    (CategoryHelper.isDylinLinearTechnologyCategoryTree(categoryTree) ||
      CategoryHelper.isBarstockCategoryTree(categoryTree))
  ) {
    return false;
  }
  return originalValue;
};

/**
 * Determines whether to show cutting cost information in the price box for a given locale and category.
 * The PriceBox is part of the product teasers on category pages.
 *
 * Requirements for these implementations come from COM-9549, COM-9673, COM-9407 (  and maybe others ;)  )
 *
 * @param {Locale} locale - The locale object containing the region information.
 * @param {string} priceCountry - The price country of the session.
 * @param {Category} categoryTree - The category tree object to verify against predefined categories.
 * @returns {boolean} Returns `true` if the cutting cost information should be shown in the price box
 *                    for the provided locale and category; otherwise, returns `false`.
 */
export const showCuttingCostInformationInPriceBox = (
  locale: Locale,
  priceCountry: string,
  categoryTree: Category
): boolean => {
  const REGIONS_WITHOUT_CUTTING_COST_TEXT_IN_PRICE_BOX = ['GB', 'IN', 'IE', 'US', 'CA', 'MX']; // regions of locales, GB is correct.
  const PRICE_COUNTRIES_WITHOUT_CUTTING_COST_TEXT_IN_PRICE_BOX = ['UK', 'IN', 'IE', 'US', 'CA', 'MX']; // priceCountries from the session, UK is correct.

  // if there is no region (like for .eu), consider the priceCountry of the session
  const isRegionOrCountryWithoutCuttingCostsText = locale.region
    ? REGIONS_WITHOUT_CUTTING_COST_TEXT_IN_PRICE_BOX.includes(locale.region)
    : PRICE_COUNTRIES_WITHOUT_CUTTING_COST_TEXT_IN_PRICE_BOX.includes(priceCountry.toUpperCase());

  return !(
    (CategoryHelper.isDylinLinearTechnologyCategoryTree(categoryTree) ||
      CategoryHelper.isEchainCategoryTree(categoryTree) ||
      CategoryHelper.isBarstockCategoryTree(categoryTree)) &&
    isRegionOrCountryWithoutCuttingCostsText
  );
};
